import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, Modal, TouchableOpacity, ActivityIndicator, ScrollView, Alert, Platform, Pressable } from 'react-native';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { studentAPI } from '../../services/api';
import { Toast } from '../common/Toast';
import { FormRenderer } from './FormRenderer';
import { studentFormSections, parentFormSection, financialAgreementSection, Field, Section } from './studentForm.config';
import { parseBackendDateOnly } from '../../utils/dateUtils';

const ALL_SECTIONS: Section[] = [...studentFormSections, ...parentFormSection, ...financialAgreementSection];

// A required field only blocks submission while it's actually visible — the same
// condition/dependsOn+showWhen rules FormRenderer uses to decide what to render.
function isFieldVisible(field: Field, formData: Record<string, any>): boolean {
  if (field.condition && !field.condition(formData)) return false;
  if (field.dependsOn && field.showWhen && !field.showWhen(formData[field.dependsOn])) return false;
  return true;
}

// Mirrors the web frontend's mutually-exclusive "Is Emergency Contact" /
// "Is Financial Contract" checkbox sync (EditProfile.tsx) — checking one on a
// relative copies their details into the Emergency Contact / Financial Contract
// section and locks those fields read-only there.
const RELATIVE_PREFIXES = ['mother', 'father', 'guardian'] as const;
const EMERGENCY_FIELD_SUFFIXES = ['FirstName', 'MiddleName', 'LastName', 'Email', 'PhoneHome', 'PhoneWork', 'PhoneCell', 'Address', 'PoBox'];
const CONTRACT_FIELD_MAP: Record<string, string> = {
  PoBox: 'authorizationPoBox',
  PhoneHome: 'authorizationPhoneHome',
  PhoneWork: 'authorizationPhoneWork',
  PhoneCell: 'authorizationPhoneCell',
  Email: 'authorizationEmail',
  Address: 'authorizationAddress',
};
const EMERGENCY_TARGET_FIELDS = EMERGENCY_FIELD_SUFFIXES.map(s => `emergency${s}`);
const CONTRACT_TARGET_FIELDS = ['authorizationName', 'relationship', ...Object.values(CONTRACT_FIELD_MAP)];

function isRelativeFlagged(v: any): boolean {
  return v === true || ["true", "1", "yes", "y"].includes(String(v).toLowerCase().trim());
}

// The one place that decides whether a relative record IS the mother/father/
// guardian, by relationCode (including the legacy numeric '2'/'1' codes) or
// by relationName. Every lookup that needs to tell a mother/father/guardian
// row apart from a standalone "OTHER" contact must go through this — a
// mismatch here (e.g. matching 'MOTHER' exactly in one place but accepting
// '2' in another) makes code fail to find the real mother/father row, insert
// a duplicate, and leave the real row an orphan that looks like a standalone
// contact and gets deactivated.
const RELATIVE_CODE_MATCHERS: Record<string, (r: any) => boolean> = {
  mother: (r) => ['MOTHER', '2'].includes(String(r.relationCode ?? r.RelationCode ?? '').toUpperCase()) || String(r.relationName ?? r.RelationName ?? '').toLowerCase().includes('mother'),
  father: (r) => ['FATHER', '1'].includes(String(r.relationCode ?? r.RelationCode ?? '').toUpperCase()) || String(r.relationName ?? r.RelationName ?? '').toLowerCase().includes('father'),
  guardian: (r) => ['GUARDIAN'].includes(String(r.relationCode ?? r.RelationCode ?? '').toUpperCase()) || String(r.relationName ?? r.RelationName ?? '').toLowerCase().includes('guardian'),
};

function isMotherFatherGuardian(r: any): boolean {
  return RELATIVE_CODE_MATCHERS.mother(r) || RELATIVE_CODE_MATCHERS.father(r) || RELATIVE_CODE_MATCHERS.guardian(r);
}

function capitalize(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function syncEmergencyFromPrefix(data: Record<string, any>, prefix: string) {
  const updates: Record<string, any> = {};
  EMERGENCY_FIELD_SUFFIXES.forEach(suffix => {
    updates[`emergency${suffix}`] = data[`${prefix}${suffix}`] ?? '';
  });
  return updates;
}

function syncContractFromPrefix(data: Record<string, any>, prefix: string) {
  const updates: Record<string, any> = {
    authorizationName: `${data[`${prefix}FirstName`] || ''} ${data[`${prefix}LastName`] || ''}`.trim(),
    relationship: capitalize(prefix),
  };
  Object.entries(CONTRACT_FIELD_MAP).forEach(([suffix, target]) => {
    updates[target] = data[`${prefix}${suffix}`] ?? '';
  });
  return updates;
}

function clearFields(fields: string[]) {
  const updates: Record<string, any> = {};
  fields.forEach(f => { updates[f] = ''; });
  return updates;
}

interface Props {
  visible: boolean;
  onClose: () => void;
  studentData: any;
  onSuccess: () => void;
  isScreen?: boolean;
  hideCloseButton?: boolean;
}

const getInitialState = () => {
  const state: Record<string, any> = {};
  const allSections = [...studentFormSections, ...parentFormSection, ...financialAgreementSection];
  allSections.forEach(section => {
    section.fields.forEach(field => {
      if (field.name) {
        state[field.name] = field.type === 'checkbox' ? false : '';
      }
    });
  });
  return state;
};

// isEmergency/isContract must be each relative's own raw isEmergencyContact/
// isBillingContact flag (see isRelativeFlagged), never "is this the relative
// the caller resolved as the fallback emergency/financial contact" — the
// `emergency` lookup falls back to guardian when nobody is explicitly
// flagged, purely to pre-fill the Emergency Contact section with a starting
// point. Deriving the checkbox from that equality instead of the raw flag
// used to tick guardianIsEmergency automatically whenever nobody had
// actually checked it, which locked the Emergency Contact fields read-only
// and made it impossible to enter a genuinely different contact.
const mapRelativeFields = (prefix: string, relative: any, isEmergency: boolean, isContract: boolean) => {
  if (!relative) return {};
  return {
    [`${prefix}FirstName`]: relative.firstName || relative.FirstName || '',
    [`${prefix}MiddleName`]: relative.middleName || relative.MiddleName || '',
    [`${prefix}LastName`]: relative.lastName || relative.LastName || '',
    [`${prefix}Email`]: relative.email || relative.Email || relative.personalEmail || '',
    [`${prefix}PhoneHome`]: relative.phone || relative.phoneHome || relative.HomePhone || '',
    [`${prefix}PhoneWork`]: relative.workPhone || relative.phoneWork || '',
    [`${prefix}PhoneCell`]: relative.cellPhone || relative.phoneCell || '',
    [`${prefix}Address`]: relative.address || relative.physicalAddress || '',
    [`${prefix}PoBox`]: relative.poBox || relative.PoBox || '',
    [`${prefix}Occupation`]: relative.occupation || relative.Occupation || '',
    [`${prefix}IsEmergency`]: isEmergency,
    [`${prefix}IsContract`]: isContract,
  };
};

export function EditStudentProfileModal({ visible, onClose, studentData, onSuccess, isScreen, hideCloseButton }: Props) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<Record<string, any>>(getInitialState());
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  // IDs of the emergency/financial contact when they're a standalone relative
  // (not the mother/father/guardian already tracked above) — mapRelativeFields
  // only copies display fields into formData, never an id, so without these the
  // save handler had no way to tell "this is the same person, update them" from
  // "this is a new contact", and pushed a brand-new relative on every save.
  const [emergencyContactId, setEmergencyContactId] = useState<any>(null);
  const [financialContactId, setFinancialContactId] = useState<any>(null);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const hasErrors = Object.values(fieldErrors).some(err => err !== '');

  useEffect(() => {
    if (visible && studentData) {
      const raw = studentData.rawSnapshot || {};
      const profile = raw.Profile || raw.profile || {};
      const relatives = raw.Relatives || raw.relatives || [];
      const questions = raw.Questions || raw.questions || [];

      // Find relatives
      const mother = relatives.find(RELATIVE_CODE_MATCHERS.mother);
      const father = relatives.find(RELATIVE_CODE_MATCHERS.father);
      const guardian = relatives.find(RELATIVE_CODE_MATCHERS.guardian);
      const emergency = relatives.find((r: any) => ["true", "1", "yes", "y"].includes(String(r.isEmergencyContact).toLowerCase().trim()) || r.isEmergencyContact === true) || guardian;
      const financialContract = relatives.find((r: any) => ["true", "1", "yes", "y"].includes(String(r.isBillingContact || r.isFinancialContract).toLowerCase().trim()) || r.isBillingContact === true || r.isFinancialContract === true);

      // Map questions
      const questionMap: Record<string, string> = {
        BEHAV_PROB: "behavioralProblem", FOOD_ALLERGY: "foodAllergies", MED_ALLERGY: "medicationAllergies",
        MED_COND: "medicalConditions", TAKE_MEDS: "takingMedications", LEARN_DISORDER: "learningDisorder",
        SPECIAL_NEEDS: "specialNeeds", LIVE_WITH: "studentLivesWith", PICKUP_RESP: "pickupResponsibility",
        FEE_PAYER: "feeResponsibility", REF_SOURCE: "referralSource", OTH_SCHOOLS: "placementSource"
      };
      const qData: any = {};
      questions.forEach((q: any) => {
        const field = questionMap[q.questionCode];
        if (!field) return;
        if (q.responseType === 'YesNo') qData[field] = q.answerYesNo ? "true" : "false";
        if (q.questionType === 'YESNO_DETAILS') qData[`${field}Details`] = q.answerText || "";
        if (q.responseType === 'Text') qData[field] = q.answerText || "";
      });

      // Extract the calendar day directly from the string rather than round-tripping
      // through `new Date(...).toISOString()`: the backend serializes DateOfBirth
      // without a timezone offset, so `new Date()` reads it as LOCAL time and
      // `toISOString()` then converts that back to UTC, shifting the day by the
      // device's offset. parseBackendDateOnly instead reads the YYYY-MM-DD prefix
      // as-is, ignoring any time component.
      const dob = parseBackendDateOnly(profile.DateOfBirth || profile.dateOfBirth);
      const dobStr = dob
        ? `${dob.getFullYear()}-${String(dob.getMonth() + 1).padStart(2, '0')}-${String(dob.getDate()).padStart(2, '0')}`
        : '';

      setFormData(prev => ({
        ...prev,
        firstName: profile.FirstName || profile.firstName || '',
        middleName: profile.MiddleName || profile.middleName || '',
        lastName: profile.LastName || profile.lastName || '',
        dobDay: dobStr,
        gender: (profile.Gender || profile.gender || '').toLowerCase().startsWith('f') ? 'Female' : 'Male',
        email: profile.StudentEmail || profile.studentEmail || '',
        cellPhone: profile.StudentPhone || profile.studentPhone || '',
        placeOfBirth: profile.BirthPlace || profile.birthPlace || profile.placeOfBirth || '',
        nationality: profile.Nationality || profile.nationality || '',
        religiousAffiliation: profile.Religion || profile.religion || '',
        baptized: (profile.IsBaptized || profile.isBaptized) ? 'Yes' : 'No',
        adventistChurchAffiliation: profile.ReligiousAffiliation || profile.religiousAffiliation || '',
        churchFinancialSupport: (profile.IsReligiousFinancialSupport || profile.isReligiousFinancialSupport) ? 'Yes' : 'No',
        physicalAddress: profile.Address1 || profile.address1 || '',
        district: profile.District || profile.district || '',
        poBox: profile.PoBox || profile.poBox || '',
        zipCode: profile.Pincode || profile.pincode || profile.zipCode || '',
        residenceStatus: profile.VisaStatus || profile.visaStatus || profile.residenceStatus || '',
        previousSchoolName: profile.PreviousInstitution || profile.previousInstitution || '',
        lastGradeCompleted: profile.LastGradeCompleted || profile.lastGradeCompleted || '',
        previousSchoolPhone: profile.PreviousInstitutionContact || profile.previousInstitutionContact || '',
        previousSchoolAddress: profile.PreviousInstitutionAddress || profile.previousInstitutionAddress || '',

        ...qData,
        // Each relative's own IsEmergency/IsContract checkbox reflects only that
        // relative's own saved flag — never whether they happen to be the
        // resolved fallback below. `emergency` falls back to guardian when no
        // one is explicitly flagged (matching the web form), but that fallback
        // is only meant to pre-fill the Emergency Contact section with a
        // starting point; it must not silently tick guardianIsEmergency, or
        // parents are locked out of typing in a genuinely different contact.
        ...mapRelativeFields('mother', mother, isRelativeFlagged(mother?.isEmergencyContact), isRelativeFlagged(mother?.isBillingContact ?? mother?.isFinancialContract)),
        ...mapRelativeFields('father', father, isRelativeFlagged(father?.isEmergencyContact), isRelativeFlagged(father?.isBillingContact ?? father?.isFinancialContract)),
        ...mapRelativeFields('guardian', guardian, isRelativeFlagged(guardian?.isEmergencyContact), isRelativeFlagged(guardian?.isBillingContact ?? guardian?.isFinancialContract)),
        ...mapRelativeFields('emergency', emergency, isRelativeFlagged(emergency?.isEmergencyContact), isRelativeFlagged(emergency?.isBillingContact ?? emergency?.isFinancialContract)),

        authorizationName: financialContract ? `${financialContract.firstName || financialContract.FirstName || ''} ${financialContract.lastName || financialContract.LastName || ''}`.trim() : '',
        authorizationEmail: financialContract?.email || financialContract?.personalEmail || '',
        authorizationPhoneHome: financialContract?.phone || financialContract?.phoneHome || '',
        authorizationPhoneWork: financialContract?.workPhone || financialContract?.phoneWork || '',
        authorizationPhoneCell: financialContract?.cellPhone || financialContract?.phoneCell || '',
        authorizationAddress: financialContract?.address || financialContract?.physicalAddress || '',
        authorizationPoBox: financialContract?.poBox || '',
      }));
      // Only remember this id for reuse when the loaded emergency/financial
      // contact is itself a standalone (non-mother/father/guardian) relative.
      // If it's currently mother/father/guardian - e.g. `emergency` falling
      // back to guardian - then unchecking that box and typing a different
      // "other" contact must INSERT a new relative, not patch this id: reusing
      // a family member's id here made the save overwrite their name,
      // relationCode, and details with the new "other" contact's data,
      // destroying their record instead of creating a separate person.
      setEmergencyContactId(emergency && !isMotherFatherGuardian(emergency) ? (emergency.id ?? emergency.Id ?? null) : null);
      setFinancialContactId(financialContract && !isMotherFatherGuardian(financialContract) ? (financialContract.id ?? financialContract.Id ?? null) : null);
    } else {
      setFormData(getInitialState());
      setEmergencyContactId(null);
      setFinancialContactId(null);
    }
  }, [visible, studentData]);

  const handleFieldChange = (name: string, value: any) => {
    setFieldErrors(prev => ({ ...prev, [name]: '' }));

    setFormData(prev => {
      let next: Record<string, any> = { ...prev, [name]: value };

      const emergencyMatch = RELATIVE_PREFIXES.find(p => name === `${p}IsEmergency`);
      if (emergencyMatch) {
        if (value) {
          const existing = RELATIVE_PREFIXES.find(p => p !== emergencyMatch && prev[`${p}IsEmergency`]);
          if (existing) {
            setFieldErrors(errs => ({ ...errs, [name]: `${capitalize(existing)} is already selected as Emergency Contact.` }));
            return prev;
          }
          if (!prev[`${emergencyMatch}FirstName`] || !prev[`${emergencyMatch}LastName`] || !prev[`${emergencyMatch}Email`] || !prev[`${emergencyMatch}PhoneHome`]) {
            setFieldErrors(errs => ({ ...errs, [name]: `Please fill First Name, Last Name, E-mail, and Phone (Home) for ${capitalize(emergencyMatch)} first.` }));
            return prev;
          }
          next = { ...next, ...syncEmergencyFromPrefix(next, emergencyMatch) };
        } else if (!RELATIVE_PREFIXES.some(p => next[`${p}IsEmergency`])) {
          next = { ...next, ...clearFields(EMERGENCY_TARGET_FIELDS) };
        }
        return next;
      }

      const contractMatch = RELATIVE_PREFIXES.find(p => name === `${p}IsContract`);
      if (contractMatch) {
        if (value) {
          const existing = RELATIVE_PREFIXES.find(p => p !== contractMatch && prev[`${p}IsContract`]);
          if (existing) {
            setFieldErrors(errs => ({ ...errs, [name]: `${capitalize(existing)} is already selected as Financial Contract.` }));
            return prev;
          }
          if (!prev[`${contractMatch}FirstName`] || !prev[`${contractMatch}LastName`] || !prev[`${contractMatch}Email`] || !prev[`${contractMatch}PhoneHome`]) {
            setFieldErrors(errs => ({ ...errs, [name]: `Please fill First Name, Last Name, E-mail, and Phone (Home) for ${capitalize(contractMatch)} first.` }));
            return prev;
          }
          next = { ...next, ...syncContractFromPrefix(next, contractMatch) };
        } else if (!RELATIVE_PREFIXES.some(p => next[`${p}IsContract`])) {
          next = { ...next, ...clearFields(CONTRACT_TARGET_FIELDS) };
        }
        return next;
      }

      // Live-sync: editing a mother/father/guardian field that's currently the
      // designated emergency contact / financial contract keeps the target section in sync.
      const sourcePrefix = RELATIVE_PREFIXES.find(p =>
        EMERGENCY_FIELD_SUFFIXES.some(suffix => name === `${p}${suffix}`)
      );
      if (sourcePrefix) {
        if (next[`${sourcePrefix}IsEmergency`]) next = { ...next, ...syncEmergencyFromPrefix(next, sourcePrefix) };
        if (next[`${sourcePrefix}IsContract`]) next = { ...next, ...syncContractFromPrefix(next, sourcePrefix) };
      }

      return next;
    });
  };

  const handleFieldBlur = (name: string, value: any) => {
    let error = '';
    const field = ALL_SECTIONS.flatMap(s => s.fields).find(f => f.name === name);
    
    if (field && value && typeof value === 'string') {
      const trimmed = value.trim();
      if (field.type === 'email' && trimmed) {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!emailRegex.test(trimmed)) {
          error = 'Invalid email address';
        }
      } else if (field.type === 'tel' && trimmed) {
        const phoneDigits = trimmed.replace(/[^0-9]/g, "");
        if (phoneDigits.length > 0 && (phoneDigits.length < 7 || phoneDigits.length > 15)) {
          error = 'Invalid phone number';
        }
      }
    }
    
    if (error) {
      setFieldErrors(prev => ({ ...prev, [name]: error }));
    }
  };

  const handleSubmit = async () => {
    const newFieldErrors: Record<string, string> = {};
    ALL_SECTIONS.forEach(section => {
      if (section.condition && !section.condition(formData)) return;
      section.fields.forEach(field => {
        if (!field.name) return;
        if (!isFieldVisible(field, formData)) return;
        const value = formData[field.name];
        const trimmedValue = typeof value === 'string' ? value.trim() : value;

        if (field.required && (trimmedValue === undefined || trimmedValue === null || trimmedValue === '')) {
          newFieldErrors[field.name] = `${field.label} is required`;
        } else if (trimmedValue && typeof trimmedValue === 'string') {
          if (field.type === 'email') {
            const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
            if (!emailRegex.test(trimmedValue)) newFieldErrors[field.name] = 'Invalid email address';
          } else if (field.type === 'tel') {
            const phoneDigits = trimmedValue.replace(/[^0-9]/g, "");
            if (phoneDigits.length > 0 && (phoneDigits.length < 7 || phoneDigits.length > 15)) {
              newFieldErrors[field.name] = 'Invalid phone number';
            }
          }
        }
      });
    });

    if (Object.keys(newFieldErrors).length > 0 || hasErrors) {
      setFieldErrors(prev => ({ ...prev, ...newFieldErrors }));
      return;
    }

    try {
      setLoading(true);
      const raw = studentData.rawSnapshot || {};
      const existingProfile = raw.Profile || raw.profile || {};
      const enrollments = raw.Enrollments || raw.enrollments || [];
      const existingRelatives = raw.Relatives || raw.relatives || [];
      const activeEnrollment = enrollments.find((e: any) => e.IsActive ?? e.isActive) || enrollments[0] || {};

      let dobISO = existingProfile.DateOfBirth || existingProfile.dateOfBirth;
      if (formData.dobDay) {
        try { dobISO = new Date(formData.dobDay).toISOString(); } catch (e) { }
      }

      const updatedProfile = {
        ...existingProfile,
        firstName: formData.firstName,
        middleName: formData.middleName,
        lastName: formData.lastName,
        dateOfBirth: dobISO,
        gender: formData.gender,
        studentEmail: formData.email,
        studentPhone: formData.cellPhone,
        birthPlace: formData.placeOfBirth,
        nationality: formData.nationality,
        religion: formData.religiousAffiliation === 'Other' ? formData.otherReligiousAffiliation : formData.religiousAffiliation,
        isBaptized: formData.baptized === 'Yes',
        religiousAffiliation: formData.adventistChurchAffiliation,
        isReligiousFinancialSupport: formData.churchFinancialSupport === 'Yes',
        address1: formData.physicalAddress,
        district: formData.district,
        poBox: formData.poBox,
        pincode: formData.zipCode,
        visaStatus: formData.residenceStatus === 'Other' ? formData.otherResidenceStatus : formData.residenceStatus,
        previousInstitution: formData.previousSchoolName,
        previousInstitutionContact: formData.previousSchoolPhone,
        previousInstitutionAddress: formData.previousSchoolAddress,
      };

      const relativesMap = new Map();
      existingRelatives.forEach((r: any) => relativesMap.set(r.id, r));

      const extractRelative = (prefix: 'mother' | 'father' | 'guardian', relationCode: string) => {
        if (!formData[`${prefix}FirstName`]) return null;
        let matched = existingRelatives.find(RELATIVE_CODE_MATCHERS[prefix]);
        return {
          id: matched?.id,
          firstName: formData[`${prefix}FirstName`],
          middleName: formData[`${prefix}MiddleName`],
          lastName: formData[`${prefix}LastName`],
          email: formData[`${prefix}Email`],
          phone: formData[`${prefix}PhoneHome`],
          workPhone: formData[`${prefix}PhoneWork`],
          cellPhone: formData[`${prefix}PhoneCell`],
          address: formData[`${prefix}Address`],
          poBox: formData[`${prefix}PoBox`],
          occupation: formData[`${prefix}Occupation`],
          relationCode: relationCode,
          isEmergencyContact: !!formData[`${prefix}IsEmergency`],
          isBillingContact: !!formData[`${prefix}IsContract`],
          isActive: true
        };
      };

      const updatedRelatives: any[] = [];
      const m = extractRelative('mother', 'MOTHER'); if (m) updatedRelatives.push(m);
      const f = extractRelative('father', 'FATHER'); if (f) updatedRelatives.push(f);
      const g = extractRelative('guardian', 'GUARDIAN'); if (g) updatedRelatives.push(g);

      // Whether the Emergency Contact / Financial Contract section describes
      // a standalone "other" person, versus just mirroring mother/father/
      // guardian, is decided by their own checkbox — never by comparing
      // names. Name-matching against updatedRelatives (which held mother/
      // father/guardian) used to misattribute a standalone contact's flag to
      // a relative who merely happened to share the same typed name, and
      // failed to find a genuinely standalone contact whose name had since
      // been edited. When a checkbox IS ticked, that relative's own row
      // (pushed above via extractRelative) already carries the flag; the
      // section fields are just a synced mirror of their data, not a
      // separate person.
      const anyEmergencyChecked = RELATIVE_PREFIXES.some(p => formData[`${p}IsEmergency`]);
      const anyContractChecked = RELATIVE_PREFIXES.some(p => formData[`${p}IsContract`]);

      let standaloneEmergency: any = null;
      if (!anyEmergencyChecked && formData.emergencyFirstName) {
        standaloneEmergency = {
          id: emergencyContactId ?? undefined,
          firstName: formData.emergencyFirstName,
          middleName: formData.emergencyMiddleName,
          lastName: formData.emergencyLastName,
          email: formData.emergencyEmail,
          phone: formData.emergencyPhoneHome,
          workPhone: formData.emergencyPhoneWork,
          cellPhone: formData.emergencyPhoneCell,
          address: formData.emergencyAddress,
          poBox: formData.emergencyPoBox,
          relationCode: 'OTHER',
          isEmergencyContact: true,
          isBillingContact: false,
          isActive: true,
        };
      }

      let standaloneContract: any = null;
      if (!anyContractChecked && formData.authorizationName) {
        const parts = formData.authorizationName.split(' ');
        standaloneContract = {
          id: financialContactId ?? undefined,
          firstName: parts[0],
          lastName: parts.slice(1).join(' '),
          email: formData.authorizationEmail,
          phone: formData.authorizationPhoneHome,
          workPhone: formData.authorizationPhoneWork,
          cellPhone: formData.authorizationPhoneCell,
          address: formData.authorizationAddress,
          poBox: formData.authorizationPoBox,
          relationCode: 'OTHER',
          isEmergencyContact: false,
          isBillingContact: true,
          isActive: true,
        };
      }

      // The same standalone person can be both the Emergency Contact and the
      // Financial Contract. Sending two separate patch entries for the same
      // relative id would have the second silently clear the flag the first
      // just set (each entry sets isEmergencyContact/isBillingContact
      // outright), so merge them into one push whenever they're clearly the
      // same person — same existing id, or same freshly-typed name when
      // neither has an id yet.
      const sameStandalonePerson = !!standaloneEmergency && !!standaloneContract && (
        (!!standaloneEmergency.id && standaloneEmergency.id === standaloneContract.id) ||
        (!standaloneEmergency.id && !standaloneContract.id &&
          standaloneEmergency.firstName === standaloneContract.firstName &&
          standaloneEmergency.lastName === standaloneContract.lastName)
      );

      if (sameStandalonePerson) {
        updatedRelatives.push({ ...standaloneEmergency, ...standaloneContract, isEmergencyContact: true, isBillingContact: true });
      } else {
        if (standaloneEmergency) updatedRelatives.push(standaloneEmergency);
        if (standaloneContract) updatedRelatives.push(standaloneContract);
      }

      // updatedRelatives only ever holds mother/father/guardian plus whichever
      // standalone person is now the emergency/financial contact. Any OTHER
      // relative that the backend currently has flagged true (e.g. a previous
      // standalone contact who has since been replaced) never appears in this
      // payload at all — and the backend only patches the ids it's given, so
      // their stale flag would otherwise never get cleared no matter how many
      // times this form is saved. Explicitly include them with the flag turned
      // off so a save actually supersedes whoever held it before. A standalone
      // OTHER contact only ever existed to fill that one role, so any OTHER
      // relative left out of updatedRelatives — whether its flag is still
      // stuck true from a past save, or was already cleared by an even
      // earlier one that predates this cleanup — is deactivated outright
      // rather than left behind as a permanent, badge-less entry cluttering
      // the Family & Contacts list. Mother/father/guardian stay active family
      // members regardless of which checkbox is ticked, so they're only ever
      // flag-cleared, never deactivated.
      const includedIds = new Set(updatedRelatives.map(r => r.id).filter(Boolean));
      existingRelatives.forEach((r: any) => {
        const id = r.id || r.Id;
        if (!id || includedIds.has(id)) return;
        const relationCode = r.relationCode || r.RelationCode || 'OTHER';
        const isStandalone = !isMotherFatherGuardian(r);
        const wasEmergency = isRelativeFlagged(r.isEmergencyContact ?? r.IsEmergencyContact);
        const wasBilling = isRelativeFlagged(r.isBillingContact ?? r.isFinancialContract ?? r.IsBillingContact);
        if (isStandalone) {
          updatedRelatives.push({ id, relationCode, isEmergencyContact: false, isBillingContact: false, isActive: false });
          includedIds.add(id);
        } else if (wasEmergency || wasBilling) {
          updatedRelatives.push({ id, relationCode, isEmergencyContact: false, isBillingContact: false });
          includedIds.add(id);
        }
      });

      const questionMap: Record<string, string> = {
        BEHAV_PROB: "behavioralProblem", FOOD_ALLERGY: "foodAllergies", MED_ALLERGY: "medicationAllergies",
        MED_COND: "medicalConditions", TAKE_MEDS: "takingMedications", LEARN_DISORDER: "learningDisorder",
        SPECIAL_NEEDS: "specialNeeds", LIVE_WITH: "studentLivesWith", PICKUP_RESP: "pickupResponsibility",
        FEE_PAYER: "feeResponsibility", REF_SOURCE: "referralSource", OTH_SCHOOLS: "placementSource"
      };

      const questionResponses: any[] = [];
      Object.entries(questionMap).forEach(([code, field]) => {
        const val = formData[field];
        const details = formData[`${field}Details`];
        if (val) {
          questionResponses.push({
            questionCode: code,
            answerYesNo: val === "true" ? true : val === "false" ? false : null,
            answerText: details || (val !== "true" && val !== "false" ? val : null),
            isActive: true
          });
        }
      });

      const payload = {
        ...updatedProfile,
        relatives: updatedRelatives,
        questionResponses: questionResponses,
      };

      await studentAPI.updateStudentProfile(studentData.id, payload);

      if (Platform.OS === 'web') {
        onSuccess();
        onClose();
        setTimeout(() => {
          Alert.alert('Success', 'Profile updated successfully');
        }, 100);
      } else {
        Alert.alert('Success', 'Profile updated successfully', [
          {
            text: 'OK',
            onPress: () => {
              onSuccess();
              onClose();
            }
          }
        ]);
      }
    } catch (err: any) {
      console.error(err);
      const message = err?.response?.data?.error || err?.response?.data?.message || 'Failed to update profile. Please try again.';
      setErrorMessage(message);
      setShowErrorToast(true);
    } finally {
      setLoading(false);
    }
  };

  const content = (
    <View style={isScreen ? { flex: 1, backgroundColor: '#FFF' } : styles.modalContent}>
      <TouchableOpacity activeOpacity={1} style={{ flex: 1, width: '100%' }}>
        <View style={isScreen ? styles.screenHeader : styles.modalHeader}>
          {isScreen && !hideCloseButton && (
            <TouchableOpacity onPress={onClose} style={styles.iconButton}>
              <FeatherIcon name="chevron-left" size={20} color="rgb(30, 41, 59)" />
            </TouchableOpacity>
          )}
          {isScreen && hideCloseButton && (
            <View style={{ width: 40 }} />
          )}
          <Text style={isScreen ? styles.screenTitle : styles.modalTitle}>Edit Full Student Profile</Text>
          {isScreen ? (
            <View style={{ width: 40 }} />
          ) : (
            !hideCloseButton && (
              <TouchableOpacity onPress={onClose} style={styles.closeButton}>
                <FeatherIcon name="x" size={24} color="#64748B" />
              </TouchableOpacity>
            )
          )}
        </View>

        <ScrollView style={styles.scrollArea} contentContainerStyle={styles.modalBody}>
          {/* A validation error (e.g. "Mother is already selected as Emergency
              Contact") is only cleared when the SAME field changes again, so it
              stays on screen indefinitely otherwise. This Pressable only catches
              taps that land on empty space between/around fields — an actual
              input/checkbox consumes its own tap first, so this never interferes
              with any field's own onChange/onBlur clearing — and just hides
              whatever errors are currently shown without touching the validation
              in handleFieldChange, which re-runs and re-blocks exactly as before
              the next time that checkbox is pressed. */}
          <Pressable onPress={() => setFieldErrors({})} style={{ flex: 1 }}>
            {studentFormSections.map((section, idx) => (
              <FormRenderer key={`student-${idx}`} section={section} formData={formData} onChange={handleFieldChange} onBlur={handleFieldBlur} errors={fieldErrors} />
            ))}
            {parentFormSection.map((section, idx) => (
              <FormRenderer key={`parent-${idx}`} section={section} formData={formData} onChange={handleFieldChange} onBlur={handleFieldBlur} errors={fieldErrors} />
            ))}
            {financialAgreementSection.map((section, idx) => (
              <FormRenderer key={`finance-${idx}`} section={section} formData={formData} onChange={handleFieldChange} onBlur={handleFieldBlur} errors={fieldErrors} />
            ))}
          </Pressable>
        </ScrollView>

        <View style={styles.actions}>

          {!isScreen && !hideCloseButton && (
            <TouchableOpacity style={styles.cancelBtn} onPress={onClose}>
              <Text style={styles.cancelText}>Cancel</Text>
            </TouchableOpacity>
          )}
          <TouchableOpacity
            style={[styles.saveBtn, isScreen && { flex: 1 }, loading && styles.saveBtnDisabled]}
            onPress={handleSubmit}
            disabled={loading}
          >
            {loading ? <ActivityIndicator color="#FFF" /> : <Text style={styles.saveText}>Save</Text>}
          </TouchableOpacity>
        </View>
      </TouchableOpacity>
      <Toast
        visible={showErrorToast}
        message={errorMessage}
        type="error"
        onDismiss={() => setShowErrorToast(false)}
      />
    </View>
  );

  if (isScreen) {
    return content;
  }

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <TouchableOpacity style={styles.modalOverlay} activeOpacity={1} onPress={onClose}>
        {content}
      </TouchableOpacity>
    </Modal>
  );
}

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: '#FFF',
    borderRadius: 12,
    width: '100%',
    maxWidth: Platform.OS === 'web' ? 992 : '90%',
    maxHeight: '90%',
    overflow: 'hidden',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: 20,
    paddingBottom: 10,
  },
  screenHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 12,
    backgroundColor: '#FFF',
  },
  iconButton: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: 'rgb(255, 255, 255)',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: 'rgb(226, 232, 240)',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1E293B',
  },
  screenTitle: {
    fontSize: 17,
    fontWeight: '800',
    color: '#1E293B',
  },
  closeButton: {
    padding: 4,
  },
  modalBody: {
    paddingHorizontal: 20,
    paddingBottom: 20,
  },
  scrollArea: {
    flex: 1,
  },
  actions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    padding: 20,
    paddingTop: 24,
    borderTopWidth: 1,
    borderTopColor: '#E2E8F0',
    backgroundColor: '#FFF',
    gap: 16,
  },
  saveBtn: {
    paddingVertical: 12,
    paddingHorizontal: 32,
    borderRadius: 8,
    backgroundColor: 'rgb(30, 64, 175)', // Success green
    alignItems: 'center',
    justifyContent: 'center',
  },
  saveBtnDisabled: {
    opacity: 0.5,
  },
  saveText: {
    color: '#FFF',
    fontWeight: '700',
    fontSize: 14,
  },
  cancelBtn: {
    paddingVertical: 12,
    paddingHorizontal: 32,
    borderRadius: 8,
    backgroundColor: '#F1F5F9',
    alignItems: 'center',
    justifyContent: 'center',
  },
  cancelText: {
    color: '#475569',
    fontWeight: '700',
    fontSize: 14,
  }
});
